import axios from 'axios';
import { get } from 'lodash-es';
import { from, EMPTY } from 'rxjs';
import { filter } from 'rxjs/operators';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

export const getRate = (rateConfiguration) => {
  const getAndTransform = axios.post('/getrate', rateConfiguration).then((response) => {
    return get(response, 'data', emptyObject);
  });

  const rate$ = from(getAndTransform);
  return rate$;
};

export const createLabel = (fulfillmentPlanId, labelConfiguration) => {
  const getAndTransform = axios.post(`/createlabel/${fulfillmentPlanId}`, labelConfiguration).then((response) => {
    return get(response, 'data', emptyObject);
  });

  const rate$ = from(getAndTransform);
  return rate$;
};

/**
 * @typedef CreateReturnLabelRequest
 * @property {object} returnConfiguration
 * @property {object[]} items
 * @property {string} rmaNumber - Leaving this null will let the backend auto-assign an RMA number
 * @property {string} salesOrderId
 * @property {string} note
 */

/**
 * @param {CreateReturnLabelRequest} request
 * @returns {Promise<{ labelId: string }>}
 */
export const createReturnPromise = (request) => {
  return axios.post('/return', request).then((response) => response?.data || emptyObject);
};

/**
 * @param {CreateReturnLabelRequest} request
 * @returns {import('rxjs').Observable}
 */
export const createReturn = (request) => {
  const return$ = from(createReturnPromise(request));
  return return$;
};

const linkTokenForSaveConfigRequests = () => {
  const inFlightFpIdsTokens = {};
  const lockedFpIds = {};

  /**
   * @callback MakeSaveRequest
   * @param {string} fulfillmentPlanId
   * @param {object} labelConfiguration
   * @param {object?} meta
   * @param {boolean} meta.shouldLock - pass true if you want to block any further requests from canceling the current
   * @returns {import('rxjs').Observable}
   */

  /**
   * @param {string} endpoint
   * @returns {MakeSaveRequest}
   */
  const cancelLogicWrapper = (endpoint) => (fulfillmentPlanId, labelConfiguration, meta) => {
    if (lockedFpIds[fulfillmentPlanId]) {
      return EMPTY;
    }
    const shouldLock = meta?.shouldLock;
    if (shouldLock) {
      lockedFpIds[fulfillmentPlanId] = true;
    }

    if (inFlightFpIdsTokens[fulfillmentPlanId]) {
      inFlightFpIdsTokens[fulfillmentPlanId].cancel();
    }
    const cancelSource = axios.CancelToken.source();
    inFlightFpIdsTokens[fulfillmentPlanId] = cancelSource;

    const getAndTransform = axios
      .post(`${endpoint}/${fulfillmentPlanId}`, labelConfiguration, {
        cancelToken: cancelSource.token,
      })
      .then((response) => {
        return get(response, 'data', emptyObject);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        throw error;
      })
      .finally(() => {
        // Only clear state if this request is still the latest one. A cancelled request
        // must not delete state that was set by its successor.
        if (inFlightFpIdsTokens[fulfillmentPlanId] === cancelSource) {
          delete inFlightFpIdsTokens[fulfillmentPlanId];
          delete lockedFpIds[fulfillmentPlanId];
        }
      });

    const rate$ = from(getAndTransform).pipe(filter((res) => res));
    return rate$;
  };

  return {
    save: cancelLogicWrapper('/fulfillmentplan/saveconfig'),
  };
};

const { save } = linkTokenForSaveConfigRequests();

export const updateFulfillmentPlanConfig = save;

export const savePackageSet = (packageSet) => {
  return axios.post('/packageset', packageSet).then((response) => {
    return get(response, 'data', emptyObject);
  });
};

export const getPackageSets = () => {
  const getAndTransform = axios.get('/packageset').then((response) => {
    return get(response, 'data', emptyArray);
  });

  const packageSets$ = from(getAndTransform);
  return packageSets$;
};

export const getRateFromFulfillmentPlan = (fulfillmentPlan) => {
  const getAndTransform = axios.post('/getRate', fulfillmentPlan).then((response) => {
    return get(response, 'data', emptyObject);
  });

  const rate$ = from(getAndTransform);
  return rate$;
};

export const externalFulfillmentSaveAndRate = (fulfillmentPlanId, configuration) => {
  const post = axios.post(`fulfillmentplan/saveconfigandrateexternalfulfillment/${fulfillmentPlanId}`, configuration).then((response) => {
    return get(response, 'data', emptyObject);
  });

  const post$ = from(post);
  return post$;
};

export const deletePackageSet = (packageSetId) => {
  return axios.delete(`packageset/${packageSetId}`).then((response) => {
    return response;
  });
};

export const getAvailableShippingServices = (shipmentConfiguration) => {
  const params = {
    shipFromId: get(shipmentConfiguration, 'shipFromId'),
    shipToCity: get(shipmentConfiguration, 'shipTo.city'),
    shipToState: get(shipmentConfiguration, 'shipTo.state'),
    shipToCountry: get(shipmentConfiguration, 'shipTo.countryCode'),
    isFedexHal: get(shipmentConfiguration, 'isFedexHal', false),
    CarrierIds: get(shipmentConfiguration, 'CarrierIds', undefined),
    marketplaceId: shipmentConfiguration?.marketplaceId,
  };
  const request = axios.get('fulfillmentPlan/availableShippingServices', {
    params,
  });
  return from(request);
};

export const getAvailableShippingOptions = (labelConfiguration) => {
  const payload = {
    shipFromId: labelConfiguration.shipFromId,
    shipToCity: labelConfiguration.shipTo?.city,
    shipToState: labelConfiguration.shipTo?.state,
    shipToCountry: labelConfiguration.shipTo?.countryCode,
    serviceIds: labelConfiguration.serviceIds,
  };
  const request = axios.post('fulfillmentplan/availableShippingOptions', payload);
  return request;
};

export const getNeedsCustomsRequirements = (shippingConfiguration) => {
  const params = {
    shipFromId: get(shippingConfiguration, 'shipFromId'),
    shipToCity: get(shippingConfiguration, 'shipTo.city'),
    shipToState: get(shippingConfiguration, 'shipTo.state'),
    shipToPostalCode: get(shippingConfiguration, 'shipTo.postalCode'),
    shipToCountry: get(shippingConfiguration, 'shipTo.countryCode'),
    sellerLabelProviderId: get(shippingConfiguration, 'sellerLabelProviderId'),
    shippingServiceId: get(shippingConfiguration, 'shippingServiceId'),
  };

  return axios.get('customs/requirements', { params });
};

export const getBestWayToShip = (shippingConfiguration) => {
  const request = axios.post('best-way/calculate', { shippingConfiguration }).then((response) => {
    return get(response, 'data', emptyObject);
  });
  return from(request);
};

export const createTestLabel = (fulfillmentPlanId) => {
  const getAndTransform = axios.post(`fulfillmentplan/createtestlabel/${fulfillmentPlanId}`).then((response) => {
    return get(response, 'data', emptyObject);
  });

  const testLabel$ = from(getAndTransform);
  return testLabel$;
};
